import { useState, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  SafeAreaView,
  Image,
  ActivityIndicator,
  TouchableOpacity,
  Platform,
  Modal,
  Pressable,
} from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { StatsCard } from "../../components/StatsCard";
import { computeYearStats } from "../lib/calculations";
import { TitlesEarned } from "../../components/TitlesEarned";
import { TitleProgress } from "../../components/TitleProgress";
import { SpeedChart } from "../../components/SpeedChart";
import { BreedStandings } from "../../components/BreedStandings";
import { HistoricalRankings } from "../../components/HistoricalRankings";
import { DashFact } from "../../components/DashFact";
import { DashInsight } from "../../components/DashInsight";
import { RunCard } from "../../components/RunCard";
import { findAkcMatch } from "../lib/akc-match";
import { useAuth } from "../lib/auth";
import { getFollowStatus, followDog, unfollowDog } from "../lib/api";
import { registerForPushNotifications } from "../lib/pushNotifications";
import { getItem, setItem, removeItem } from "../lib/storage";

export const PENDING_FOLLOW_KEY = "pendingFollowDogId";

const API_URL = (process.env.EXPO_PUBLIC_API_URL || "https://dashhound.app") + "/api";

export default function PublicDogProfile() {
  // This same component backs two routes: the public root route /dog/[id] (share
  // links, notifications) and the in-tab route /(tabs)/dogs/dogview/[slug]
  // (reached by tapping a follower's dog — kept inside the Dogs tab so returning
  // is a simple in-stack pop and the tab never gets corrupted). Read whichever
  // param the active route provides.
  const { id: idParam, slug, follow: followParam, backTo, fromDogId, fromDogName, rBreed, rYear, rSize } = useLocalSearchParams<{ id?: string; slug?: string; follow?: string; backTo?: string; fromDogId?: string; fromDogName?: string; rBreed?: string; rYear?: string; rSize?: string }>();
  const id = (idParam ?? slug) as string;
  const router = useRouter();
  const { user } = useAuth();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [runYearFilter, setRunYearFilter] = useState<string>(new Date().getFullYear().toString());
  const [runYearPickerOpen, setRunYearPickerOpen] = useState(false);
  const [runSort, setRunSort] = useState<"newest" | "fastest" | "slowest">("newest");
  // Follow state — populated when logged in. Anonymous visitors see the
  // follower count (from the public profile payload) and a Follow button
  // that opens the sign-up CTA modal.
  const [followState, setFollowState] = useState<{ following: boolean; followerCount: number; isOwner: boolean } | null>(null);
  const [followBusy, setFollowBusy] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function load(attempt = 0) {
      try {
        const res = await fetch(`${API_URL}/public/dogs/${encodeURIComponent(id)}`);
        if (!res.ok) throw new Error("Profile not found or is private");
        const json = await res.json();
        if (!cancelled) { setData(json); setLoading(false); }
      } catch (err: any) {
        // The very first request after a cold-launch deep link (e.g. tapping a
        // shared link) can fail with "Network request failed" before iOS's
        // network stack is ready. Retry a few times with backoff before giving
        // up. A real "not found / private" isn't a network error, so it falls
        // straight through to the error state without retrying.
        const isNetwork = /network request failed/i.test(err?.message || "");
        if (isNetwork && attempt < 3 && !cancelled) {
          setTimeout(() => load(attempt + 1), 500 * (attempt + 1));
          return;
        }
        if (!cancelled) { setError(err.message); setLoading(false); }
      }
    }
    load();
    return () => { cancelled = true; };
  }, [id]);

  // Resolve the dog UUID from the loaded data (the URL `id` might be a slug),
  // then fetch the follow status. Re-fetches on user/dog change.
  const dogUuid = data?.dog?.id as string | undefined;
  const publicFollowerCount: number = (data?.dog?.followerCount ?? 0) as number;
  useEffect(() => {
    if (!dogUuid) { setFollowState(null); return; }
    if (!user) {
      // Anonymous visitor: still surface the count from the public payload
      // so the social-proof text + Follow CTA stay accurate.
      setFollowState({ following: false, followerCount: publicFollowerCount, isOwner: false });
      return;
    }
    getFollowStatus(dogUuid)
      .then((s) => setFollowState({ following: s.following, followerCount: s.followerCount, isOwner: s.isOwner }))
      .catch(() => setFollowState({ following: false, followerCount: publicFollowerCount, isOwner: false }));
  }, [user, dogUuid, publicFollowerCount]);

  // Auto-follow trigger: either from (a) a pending follow stashed pre-login
  // by an anonymous web visitor, or (b) `?follow=1` query param on the deep
  // link (used in shared URLs so tapping a FB/iMessage link → opens app →
  // immediately follows). No-op if already following or if the user is the owner.
  useEffect(() => {
    if (!user || !dogUuid) return;
    (async () => {
      try {
        const pending = await getItem(PENDING_FOLLOW_KEY);
        const queryFollow = followParam === "1" || followParam === "true";
        if (pending === dogUuid || queryFollow) {
          if (pending === dogUuid) await removeItem(PENDING_FOLLOW_KEY);
          // Check current status first so we don't try to follow if already following / owner
          const status = await getFollowStatus(dogUuid).catch(() => null);
          if (status && !status.isOwner && !status.following) {
            await followDog(dogUuid);
            setFollowState((s) => s ? { ...s, following: true, followerCount: s.followerCount + 1 } : { following: true, followerCount: (status.followerCount || 0) + 1, isOwner: false });
            registerForPushNotifications().catch(() => {});
          }
          // Strip the follow param so a back-nav refresh doesn't re-trigger
          if (queryFollow) {
            router.replace(`/dog/${encodeURIComponent(id)}` as any);
          }
        }
      } catch {}
    })();
  }, [user, dogUuid, followParam]);

  async function toggleFollow() {
    if (followBusy || followState?.isOwner) return;
    // Anonymous visitor — open the sign-up CTA modal instead.
    if (!user || !dogUuid) {
      if (dogUuid) await setItem(PENDING_FOLLOW_KEY, dogUuid).catch(() => {});
      setAuthModalOpen(true);
      return;
    }
    setFollowBusy(true);
    const prev = followState ?? { following: false, followerCount: 0, isOwner: false };
    // Optimistic toggle so the button feels instant.
    setFollowState({
      following: !prev.following,
      followerCount: prev.followerCount + (prev.following ? -1 : 1),
      isOwner: prev.isOwner,
    });
    try {
      if (prev.following) {
        await unfollowDog(dogUuid);
      } else {
        await followDog(dogUuid);
        // Ask for push permission on the first Follow tap (contextual ask =
        // higher accept rate than asking at app launch). Best-effort —
        // if denied or the device can't register, the follow still works,
        // user just won't get pings until they enable in settings.
        registerForPushNotifications().catch(() => {});
      }
    } catch {
      setFollowState(prev); // revert on failure
    } finally {
      setFollowBusy(false);
    }
  }

  if (loading) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8", justifyContent: "center", alignItems: "center" }}>
        <ActivityIndicator size="large" color="#1B2A4A" />
      </SafeAreaView>
    );
  }

  if (error || !data) {
    return (
      <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8", justifyContent: "center", alignItems: "center", padding: 20 }}>
        <Ionicons name="lock-closed" size={48} color="#6b7280" />
        <Text style={{ color: "#374151", fontSize: 18, fontWeight: "700", marginTop: 16 }}>Profile Not Available</Text>
        <Text style={{ color: "#6b7280", fontSize: 14, marginTop: 8, textAlign: "center" }}>{error || "This profile is private or doesn't exist."}</Text>
        <TouchableOpacity onPress={() => router.replace("/" as any)} style={{ marginTop: 20, backgroundColor: "#1B2A4A", paddingHorizontal: 24, paddingVertical: 12, borderRadius: 10 }}>
          <Text style={{ color: "#fff", fontWeight: "600" }}>Go to Dash-Hound</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  const { dog, stats, runs, rankings, avgPointsPerRun } = data;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      {/* Brand bar — always show a left nav button so no one gets stranded.
          Back destination, in priority order:
          1. `backTo=followers` (arrived by tapping a dog on someone's Followers
             page) → return to that exact Followers page. We can't use
             `router.back()` here: this profile is a root route and popping back
             into the tab stack resets the tabs to the Dashboard.
          2. In-app with history → the Dogs tab (`/dogs`).
          3. Cold-launched from a shared link (no history) → home button that
             drops the visitor into the app (dashboard if signed in, landing
             page if not). */}
      <View style={{ backgroundColor: "#1B2A4A", padding: 16, flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
        <TouchableOpacity
          onPress={() => {
            if (backTo === "rankings") {
              // Came from the Rankings tab. canGoBack() is false for this root
              // route on native, so return to Rankings with the same filters.
              if (router.canGoBack()) {
                router.back();
              } else {
                const q = [rBreed ? `breed=${encodeURIComponent(rBreed)}` : "", rYear ? `year=${encodeURIComponent(rYear)}` : "", rSize ? `size=${encodeURIComponent(rSize)}` : ""].filter(Boolean).join("&");
                router.replace(`/(tabs)/rankings${q ? "?" + q : ""}` as any);
              }
            } else if (backTo === "followers") {
              // Came from the Followers page via the in-tab dogview route, so
              // this profile was PUSHED onto the Dogs stack right above
              // Followers. A plain in-stack pop returns to Followers reliably on
              // both web and native (no tab-group crossing to confuse history).
              if (router.canGoBack()) {
                router.back();
              } else {
                const q = `dogId=${fromDogId}&dogName=${encodeURIComponent(fromDogName || "")}`;
                router.replace(`/(tabs)/dogs/followers?${q}` as any);
              }
            } else if (Platform.OS === "web" && typeof window !== "undefined" && window.history.length > 1) {
              // The dog profile is a root route, so expo-router's canGoBack() is
              // false even when the browser has history. Use real browser-back so
              // visitors return to wherever they came from (rankings, events, etc.).
              window.history.back();
            } else if (router.canGoBack()) {
              router.back();
            } else {
              router.replace("/" as any);
            }
          }}
          style={{ position: "absolute", left: 12, top: 0, bottom: 0, justifyContent: "center", paddingHorizontal: 8 }}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Ionicons name={(backTo === "rankings" || backTo === "followers" || router.canGoBack() || (Platform.OS === "web" && typeof window !== "undefined" && window.history.length > 1)) ? "chevron-back" : "home"} size={26} color="#ffffff" />
        </TouchableOpacity>

        {/* "Open in App" — mobile-web only (phones in a browser, e.g. opened from
            Facebook's in-app browser). Hidden on desktop web and inside the native
            app. Tries the dashhound:// deep link; falls back to the store if the
            app isn't installed (or the in-app browser blocks the scheme). */}
        {Platform.OS === "web" &&
          typeof navigator !== "undefined" &&
          /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && (
            <TouchableOpacity
              onPress={() => {
                const ua = navigator.userAgent;
                const store = /Android/i.test(ua)
                  ? "https://play.google.com/store/apps/details?id=com.dashhound.app"
                  : "https://apps.apple.com/app/id6762554245";
                let appOpened = false;
                const onHide = () => {
                  if (document.hidden) appOpened = true;
                };
                document.addEventListener("visibilitychange", onHide);
                window.location.href = `dashhound://dog/${id}`;
                setTimeout(() => {
                  document.removeEventListener("visibilitychange", onHide);
                  if (!appOpened) window.location.href = store;
                }, 1500);
              }}
              style={{ position: "absolute", right: 12, top: 0, bottom: 0, justifyContent: "center" }}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            >
              <View style={{ flexDirection: "row", alignItems: "center", backgroundColor: "#F5A623", paddingHorizontal: 8, paddingVertical: 4, borderRadius: 6 }}>
                <Ionicons name="phone-portrait" size={11} color="#1B2A4A" style={{ marginRight: 3 }} />
                <Text style={{ color: "#1B2A4A", fontWeight: "700", fontSize: 10.5 }}>Open in App</Text>
              </View>
            </TouchableOpacity>
          )}

        <Image source={{ uri: "https://dashhound.app/logo-text.png" }} style={{ width: 160, height: 32 }} resizeMode="contain" />
      </View>

      <ScrollView contentContainerStyle={{ padding: 20, paddingBottom: 60 }}>
        {/* Dog header card */}
        <View style={{ backgroundColor: "#1B2A4A", borderRadius: 16, padding: 20, marginBottom: 16 }}>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {dog.photo ? (
              Platform.OS === "web" ? (
                // @ts-ignore — respect owner-set focal point on public profile too
                <img
                  src={dog.photo}
                  alt={dog.name}
                  style={{ width: 72, height: 72, borderRadius: 36, border: "2px solid rgba(255,255,255,0.4)", marginRight: 14, objectFit: "cover", objectPosition: `center ${dog.photoFocusY ?? 50}%` } as any}
                />
              ) : (
                <Image source={{ uri: dog.photo }} style={{ width: 72, height: 72, borderRadius: 36, borderWidth: 2, borderColor: "rgba(255,255,255,0.4)", marginRight: 14 }} />
              )
            ) : (
              <View style={{ width: 72, height: 72, borderRadius: 36, backgroundColor: "rgba(255,255,255,0.2)", alignItems: "center", justifyContent: "center", marginRight: 14 }}>
                <Ionicons name="paw" size={32} color="rgba(255,255,255,0.6)" />
              </View>
            )}
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 24, fontWeight: "800", color: "#fff" }}>{dog.name}</Text>
              {dog.registeredName && (
                <Text style={{ fontSize: 14, color: "#94A3B8", marginTop: 2, fontStyle: "italic" }} numberOfLines={2}>"{dog.registeredName}"</Text>
              )}
              <Text style={{ fontSize: 14, color: "#94A3B8", marginTop: 3 }}>{dog.breed}</Text>
              {(dog.handicap || dog.sex) && (
                <Text style={{ fontSize: 14, color: "#94A3B8", marginTop: 2 }}>
                  {dog.sex || ""}{dog.sex && dog.handicap ? " · " : ""}{dog.handicap ? `${parseFloat(dog.handicap).toFixed(1)}× handicap` : ""}
                </Text>
              )}
            </View>

            {/* Ranking badges — official AKC rank + projected (matches in-app profile) */}
            {stats && (() => {
              const akcMatch = rankings && rankings.length > 0 ? findAkcMatch(rankings, dog.registeredName) : null;
              const projected = data.hypotheticalRank;
              if (!akcMatch && !projected) return null;
              return (
                <View style={{ alignItems: "center", marginLeft: 10, gap: 8 }}>
                  <View style={{ alignItems: "center" }}>
                    <Text style={{ fontSize: 10, color: "#94A3B8", marginBottom: 3, textTransform: "uppercase", fontWeight: "700", letterSpacing: 0.5 }}>Rank</Text>
                    <View
                      style={{
                        backgroundColor: akcMatch ? "#fbbf24" : "#4b5563",
                        borderRadius: 10,
                        paddingHorizontal: 14,
                        paddingVertical: 8,
                        alignItems: "center",
                        minWidth: 56,
                      }}
                    >
                      <Text style={{ fontSize: 20, fontWeight: "900", color: akcMatch ? "#78350f" : "#ffffff" }}>
                        {akcMatch ? `#${akcMatch.rank}` : "N/R"}
                      </Text>
                    </View>
                  </View>
                  {projected && (
                    <View style={{ alignItems: "center" }}>
                      <Text style={{ fontSize: 10, color: "#94A3B8", marginBottom: 3, textTransform: "uppercase", fontWeight: "700", letterSpacing: 0.5 }}>Projected</Text>
                      <View style={{ backgroundColor: "#fbbf24", borderRadius: 10, paddingHorizontal: 14, paddingVertical: 8, alignItems: "center", minWidth: 56 }}>
                        <Text style={{ fontSize: 20, fontWeight: "900", color: "#78350f" }}>#{projected.rank}</Text>
                      </View>
                      <Text style={{ fontSize: 10, color: "#94A3B8", marginTop: 3 }}>of {projected.total}</Text>
                    </View>
                  )}
                </View>
              );
            })()}
          </View>

          {/* Follow Me On — social links inside the navy dog header card */}
          {(() => {
            const social = (dog as any)?.social || {};
            const items = ([
              { key: "instagram", icon: "logo-instagram", color: "#E4405F", buildUrl: (v: string) => /^https?:/i.test(v) ? v : `https://instagram.com/${v.replace(/^@/, "")}` },
              { key: "facebook", icon: "logo-facebook", color: "#1877F2", buildUrl: (v: string) => /^https?:/i.test(v) ? v : `https://facebook.com/${v.replace(/^@/, "")}` },
              { key: "tiktok", icon: "logo-tiktok", color: "#000000", buildUrl: (v: string) => /^https?:/i.test(v) ? v : `https://tiktok.com/@${v.replace(/^@/, "")}` },
              { key: "x", icon: "logo-twitter", color: "#000000", buildUrl: (v: string) => /^https?:/i.test(v) ? v : `https://x.com/${v.replace(/^@/, "")}` },
              { key: "youtube", icon: "logo-youtube", color: "#FF0000", buildUrl: (v: string) => /^https?:/i.test(v) ? v : `https://youtube.com/${v.startsWith("@") ? v : "@" + v}` },
            ] as const).filter((p) => social[p.key] && String(social[p.key]).trim());
            if (items.length === 0) return null;
            return (
              <View style={{ marginTop: 14, paddingTop: 12, borderTopWidth: 1, borderTopColor: "rgba(255,255,255,0.12)", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <Text style={{ fontSize: 10, fontWeight: "800", color: "#94A3B8", textTransform: "uppercase", letterSpacing: 1, marginRight: 4 }}>
                  Follow Me On
                </Text>
                {items.map((p) => {
                  const handle = String(social[p.key]).trim();
                  const url = p.buildUrl(handle);
                  return (
                    <TouchableOpacity
                      key={p.key}
                      onPress={() => { try { (require("react-native") as any).Linking.openURL(url); } catch {} }}
                      activeOpacity={0.7}
                      style={{
                        width: 30, height: 30, borderRadius: 15,
                        backgroundColor: p.color,
                        alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <Ionicons name={p.icon as any} size={16} color="#ffffff" />
                    </TouchableOpacity>
                  );
                })}
              </View>
            );
          })()}
        </View>

        {/* Follow button + follower count. Visible to everyone — anonymous
            visitors get the sign-up CTA modal on tap. Owners see only the
            follower count, since they can't follow themselves. */}
        {followState && (
          <View style={{ marginBottom: 16 }}>
            {!followState.isOwner && (
              <TouchableOpacity
                onPress={toggleFollow}
                disabled={followBusy}
                style={{
                  backgroundColor: followState.following ? "#ffffff" : "#1B2A4A",
                  borderRadius: 12,
                  padding: 14,
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  borderWidth: followState.following ? 1 : 0,
                  borderColor: "#1B2A4A",
                  opacity: followBusy ? 0.6 : 1,
                }}
              >
                <Ionicons
                  name={followState.following ? "checkmark" : "add"}
                  size={18}
                  color={followState.following ? "#1B2A4A" : "#ffffff"}
                />
                <Text style={{ color: followState.following ? "#1B2A4A" : "#ffffff", fontSize: 16, fontWeight: "700" }}>
                  {followState.following ? "Following" : user ? "Follow" : `Follow ${dog?.name || "this dog"}`}
                </Text>
              </TouchableOpacity>
            )}
            {followState.followerCount > 0 && (
              <Text style={{ color: "#6b7280", fontSize: 13, marginTop: 8, textAlign: "center" }}>
                {followState.followerCount === 1
                  ? "1 handler follows this dog"
                  : `${followState.followerCount} handlers follow this dog`}
              </Text>
            )}
          </View>
        )}

        {/* Sign-up CTA modal — shown when an anonymous visitor taps Follow */}
        <Modal visible={authModalOpen} transparent animationType="fade" onRequestClose={() => setAuthModalOpen(false)}>
          <Pressable
            onPress={() => setAuthModalOpen(false)}
            style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.5)", justifyContent: "center", alignItems: "center", padding: 24 }}
          >
            <Pressable onPress={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 380, backgroundColor: "#fff", borderRadius: 16, padding: 24 }}>
              <View style={{ alignItems: "center", marginBottom: 16 }}>
                <View style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: "#FEF3C7", alignItems: "center", justifyContent: "center", marginBottom: 12 }}>
                  <Ionicons name="paw" size={28} color="#F5A623" />
                </View>
                <Text style={{ fontSize: 20, fontWeight: "800", color: "#1B2A4A", textAlign: "center" }}>
                  Follow {dog?.name || "this dog"}
                </Text>
                <Text style={{ fontSize: 14, color: "#6b7280", textAlign: "center", marginTop: 8, lineHeight: 20 }}>
                  Sign in or create a free Dash-Hound account to follow {dog?.name || "dogs"} and get notified every time they run.
                </Text>
              </View>
              <TouchableOpacity
                onPress={() => { setAuthModalOpen(false); router.push("/(auth)/register" as any); }}
                style={{ backgroundColor: "#1B2A4A", borderRadius: 10, paddingVertical: 14, alignItems: "center", marginBottom: 8 }}
              >
                <Text style={{ color: "#fff", fontSize: 15, fontWeight: "700" }}>Create Free Account</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => { setAuthModalOpen(false); router.push("/(auth)/login" as any); }}
                style={{ backgroundColor: "#fff", borderWidth: 1, borderColor: "#1B2A4A", borderRadius: 10, paddingVertical: 14, alignItems: "center", marginBottom: 4 }}
              >
                <Text style={{ color: "#1B2A4A", fontSize: 15, fontWeight: "700" }}>I already have an account</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setAuthModalOpen(false)} style={{ paddingVertical: 10, alignItems: "center" }}>
                <Text style={{ color: "#9ca3af", fontSize: 13 }}>Maybe later</Text>
              </TouchableOpacity>
            </Pressable>
          </Pressable>
        </Modal>

        {/* Performance stats — see (tabs)/dogs/[id].tsx for the same 3-state
            logic (current-year card / lifetime fallback / empty). */}
        {stats && (() => {
          const currentYear = new Date().getFullYear();
          const runsArr = runs || [];
          const yearStats = computeYearStats(runsArr, currentYear);
          const hasCurrentYearRuns = yearStats.totalRuns > 0;
          if (!hasCurrentYearRuns) {
            return <StatsCard stats={stats} />;
          }
          const isRookie = runsArr.every((r: any) => new Date(r.date).getFullYear() === currentYear);
          const stub: any = {
            ...stats,
            totalRuns: yearStats.totalRuns,
            personalBestTime: yearStats.personalBestTime,
            personalBestSpeed: yearStats.personalBestSpeed,
            totalPoints: yearStats.totalPoints,
            bestThreeAvgSpeed: yearStats.bestThreeAvgSpeed,
            run1AvgSpeed: yearStats.run1AvgSpeed,
            run2AvgSpeed: yearStats.run2AvgSpeed,
            run1Count: yearStats.run1Count,
            run2Count: yearStats.run2Count,
          };
          return (
            <StatsCard
              stats={stub}
              yearLabel={String(currentYear)}
              totalPointsAllTime={stats.totalPoints}
              careerStats={isRookie ? undefined : {
                totalRuns: stats.totalRuns,
                personalBestTime: stats.personalBestTime,
                personalBestSpeed: stats.personalBestSpeed,
              }}
            />
          );
        })()}

        {/* Titles earned */}
        {stats && <TitlesEarned stats={stats} />}

        {/* Title progress + ⚡ pace projection */}
        {stats && (
          <TitleProgress
            stats={stats}
            avgPointsPerRun={avgPointsPerRun || 0}
            runsLogged={runs?.length || 0}
          />
        )}

        {/* Speed over time */}
        {runs && runs.length >= 2 && <SpeedChart runs={runs} />}

        {/* Breed standings (top 5 + YOU row, with dedupe) */}
        {dog && rankings && rankings.length > 0 && (
          <BreedStandings
            rankings={rankings}
            dog={dog}
            stats={stats}
            hypotheticalRank={data.hypotheticalRank}
          />
        )}

        {/* Dash-Insight — personalized insight (publicMode hides the "Run It Here" recommendations) */}
        {dog && (
          <DashInsight
            dog={dog}
            runs={runs}
            stats={stats}
            rankings={rankings || []}
            hypotheticalRank={data.hypotheticalRank}
            dogDisplayName={dog.name}
            publicMode
          />
        )}

        {/* Dash-Fact — top 3 average run time vs this dog */}
        {dog && rankings && rankings.length > 0 && (
          <DashFact
            rankings={rankings}
            breed={dog.breed}
            userBestThreeAvgSpeed={stats?.bestThreeAvgSpeed ?? null}
            dogDisplayName={dog.name}
          />
        )}

        {/* Historical AKC rankings (per-year accordion). Pass the resolved
            dog.id (UUID) rather than the URL slug — the historical-rankings
            endpoint queries dogs by UUID. */}
        {dog && dog.id && (
          <HistoricalRankings
            dogId={String(dog.id)}
            hasRegisteredName={!!(dog.registeredName && dog.registeredName.trim())}
            currentYear={new Date().getFullYear().toString()}
            publicMode
          />
        )}

        {/* Run history — read-only on public profile, with year filter + sort */}
        {runs && runs.length > 0 && (() => {
          const pbRunId = runs.reduce(
            (best: any, r: any) => parseFloat(r.speedMph) > parseFloat(best.speedMph) ? r : best,
            runs[0],
          ).id;
          const yearsSet = new Set<string>();
          runs.forEach((r: any) => yearsSet.add(new Date(r.date).getFullYear().toString()));
          const years = Array.from(yearsSet).sort((a, b) => Number(b) - Number(a));
          const yearFiltered = runYearFilter === "all" ? runs : runs.filter((r: any) => new Date(r.date).getFullYear().toString() === runYearFilter);
          const filtered = [...yearFiltered].sort((a: any, b: any) => {
            if (runSort === "fastest") return parseFloat(b.speedMph) - parseFloat(a.speedMph);
            if (runSort === "slowest") return parseFloat(a.speedMph) - parseFloat(b.speedMph);
            return new Date(b.date).getTime() - new Date(a.date).getTime();
          });
          const showPicker = years.length >= 2;
          const headerCount = showPicker && runYearFilter !== "all"
            ? ` (${filtered.length} of ${runs.length})`
            : ` (${runs.length})`;

          return (
            <View style={{ marginBottom: 16 }}>
              <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <Text style={{ fontSize: 16, fontWeight: "700", color: "#1B2A4A" }}>Run History{headerCount}</Text>
                {showPicker && (
                  <TouchableOpacity
                    onPress={() => setRunYearPickerOpen(true)}
                    style={{ flexDirection: "row", alignItems: "center", gap: 6, backgroundColor: "#ffffff", borderRadius: 8, borderWidth: 1, borderColor: "#e5e7eb", paddingHorizontal: 12, paddingVertical: 7 }}
                  >
                    <Text style={{ fontSize: 13, fontWeight: "700", color: "#1B2A4A" }}>{runYearFilter === "all" ? "All years" : runYearFilter}</Text>
                    <Ionicons name="chevron-down" size={14} color="#6b7280" />
                  </TouchableOpacity>
                )}
              </View>

              <View style={{ flexDirection: "row", gap: 6, marginBottom: 12 }}>
                {([
                  { key: "newest", label: "Newest", icon: "time-outline" },
                  { key: "fastest", label: "Fastest", icon: "flash-outline" },
                  { key: "slowest", label: "Slowest", icon: "trending-down-outline" },
                ] as const).map((opt) => {
                  const selected = runSort === opt.key;
                  return (
                    <TouchableOpacity
                      key={opt.key}
                      onPress={() => setRunSort(opt.key)}
                      style={{
                        flex: 1, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 4,
                        paddingVertical: 8, paddingHorizontal: 6, borderRadius: 8,
                        borderWidth: 1, borderColor: selected ? "#1B2A4A" : "#e5e7eb",
                        backgroundColor: selected ? "#1B2A4A" : "#ffffff",
                      }}
                    >
                      <Ionicons name={opt.icon as any} size={14} color={selected ? "#ffffff" : "#6b7280"} />
                      <Text style={{ fontSize: 12, fontWeight: "700", color: selected ? "#ffffff" : "#374151" }}>{opt.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>

              {filtered.length === 0 ? (
                <View style={{ alignItems: "center", paddingVertical: 24, backgroundColor: "#ffffff", borderRadius: 12, borderWidth: 1, borderColor: "#e5e7eb" }}>
                  <Text style={{ color: "#6b7280", fontSize: 14 }}>No runs in {runYearFilter}</Text>
                </View>
              ) : (
                filtered.map((run: any) => (
                  <RunCard key={run.id} run={run} isPersonalBest={run.id === pbRunId} hideNotes showAdvanced />
                ))
              )}

              {/* Year picker modal */}
              <Modal visible={runYearPickerOpen} transparent animationType="fade" onRequestClose={() => setRunYearPickerOpen(false)}>
                <Pressable onPress={() => setRunYearPickerOpen(false)} style={{ flex: 1, backgroundColor: "rgba(0,0,0,0.4)", justifyContent: "center", alignItems: "center", padding: 24 }}>
                  <Pressable onPress={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 320, backgroundColor: "#fff", borderRadius: 14, overflow: "hidden" }}>
                    <Text style={{ fontSize: 14, fontWeight: "700", color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, paddingHorizontal: 16, paddingTop: 16, paddingBottom: 8 }}>
                      Filter by year
                    </Text>
                    {["all", ...years].map((y) => {
                      const selected = runYearFilter === y;
                      return (
                        <TouchableOpacity
                          key={y}
                          onPress={() => { setRunYearFilter(y); setRunYearPickerOpen(false); }}
                          style={{ flexDirection: "row", alignItems: "center", justifyContent: "space-between", paddingHorizontal: 16, paddingVertical: 14, borderTopWidth: 1, borderTopColor: "#f3f4f6" }}
                        >
                          <Text style={{ fontSize: 15, fontWeight: selected ? "700" : "500", color: selected ? "#1B2A4A" : "#374151" }}>
                            {y === "all" ? "All years" : y}
                          </Text>
                          {selected && <Ionicons name="checkmark" size={20} color="#1B2A4A" />}
                        </TouchableOpacity>
                      );
                    })}
                  </Pressable>
                </Pressable>
              </Modal>
            </View>
          );
        })()}

        {/* CTA: get the app */}
        <View style={{ backgroundColor: "#fff", borderRadius: 14, padding: 20, marginBottom: 16, borderWidth: 1, borderColor: "#e5e7eb", alignItems: "center" }}>
          <Text style={{ fontSize: 16, fontWeight: "800", color: "#1B2A4A", textAlign: "center" }}>
            Track your own dog with Dash-Hound
          </Text>
          <Text style={{ fontSize: 13, color: "#6b7280", textAlign: "center", marginTop: 6, marginBottom: 14 }}>
            Free Fast CAT tracking — runs, rankings, titles, events.
          </Text>
          <TouchableOpacity
            onPress={() => router.push("/" as any)}
            style={{ backgroundColor: "#1B2A4A", paddingHorizontal: 24, paddingVertical: 12, borderRadius: 10 }}
          >
            <Text style={{ color: "#fff", fontWeight: "700", fontSize: 14 }}>Get Started</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
